import { css, keyframes } from "@emotion/css";
import { fontSize, lineHeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import cn from "classnames";
import React, { useEffect, useState } from "react";
import { IconButtonWithTooltip } from "~/components/IconButtonWithTooltip";
import { useDidUpdateEffect } from "~/hooks/useDidUpdateEffect";
export interface HelpPanelImage {
    src: string;
    altText: string;
}
export type HelpPanelSize = "third" | "half";
interface HelpPanelProps {
    image?: HelpPanelImage;
    content?: React.ReactNode;
    size: HelpPanelSize;
    showToggle: boolean;
    theme: "light" | "dark";
    open: boolean;
    onClose: () => void;
}
export function HelpPanel({ image, content, size, showToggle, theme, open, onClose }: HelpPanelProps) {
    const [helpPanelClasses, setHelpPanelClasses] = useState([styles.helpPanel]);
    useEffect(() => {
        setHelpPanelClasses(open ? [] : [styles.displayNone]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useDidUpdateEffect(() => {
        setHelpPanelClasses(open ? [styles.animateOpen] : [styles.animateClose]);
        const timeoutId = setTimeout(() => setHelpPanelClasses(open ? [] : [styles.displayNone]), 350);
        return () => clearTimeout(timeoutId);
    }, [open]);
    const background = theme === "light" ? css({ backgroundColor: themeTokens.color.background.primary.default }) : css({});
    return (<div className={cn(...helpPanelClasses, styles.helpPanel, background, styles.size(size))}>
            {showToggle && (<span className={styles.toggleButton}>
                    <IconButtonWithTooltip toolTipContent={"Collapse help"} onClick={() => onClose()} icon="ChevronIcon"/>
                </span>)}
            {image && <img src={image.src} className={styles.image} alt={image.altText}/>}
            {content && <div className={styles.content}>{content}</div>}
        </div>);
}
interface HelpPanelToggleProps {
    showToggle: boolean;
    open: boolean;
    onClick: () => void;
}
export function HelpPanelToggle({ showToggle, open, onClick }: HelpPanelToggleProps) {
    if (open || !showToggle)
        return <></>;
    return (<span className={styles.toggleButton}>
            <IconButtonWithTooltip toolTipContent={"Expand help"} icon="OpenHelp" onClick={onClick}/>
        </span>);
}
const animations = {
    slideIn: keyframes({
        "0%": {
            marginLeft: "-40%",
            transform: "translateX(-60%)",
            boxShadow: "none",
        },
        "100%": {
            marginLeft: 0,
            transform: "translateX(0%)",
            width: "100%",
            boxShadow: themeTokens.shadow.large,
        },
    }),
    slideOut: keyframes({
        "0%": {
            marginLeft: 0,
            transform: "translateX(0%)",
            boxShadow: themeTokens.shadow.large,
        },
        "100%": {
            marginLeft: "-40%",
            transform: "translateX(-60%)",
            boxShadow: "none",
        },
    }),
};
const styles = {
    helpPanel: css({
        flex: "auto",
        position: "relative",
        overflow: "hidden",
        background: "#122536",
        borderRadius: `0 ${space[4]} ${space[4]} 0`,
        maxWidth: "400px",
        height: "100%",
        alignContent: "center",
        "@media (max-width: 1000px)": {
            display: "none",
        },
        boxShadow: themeTokens.shadow.large,
    }),
    animateClose: css({
        animation: `${animations.slideOut} 0.35s forwards`,
    }),
    animateOpen: css({
        animation: `${animations.slideIn} 0.35s forwards`,
    }),
    displayNone: css({
        display: "none",
    }),
    toggleButton: css({
        position: "absolute",
        right: "20px",
        top: "20px",
        zIndex: 10,
        "@media (max-width: 1000px)": {
            visibility: "hidden",
        },
    }),
    content: css({
        display: "block",
        height: "100%",
        width: "80.5%",
        margin: "auto",
        padding: `${space[24]} 0`,
        color: "white",
        lineHeight: lineHeight.small,
        fontWeight: 400,
        fontSize: fontSize.base,
    }),
    image: css({
        display: "block",
        width: "80.5%",
        margin: "80px auto 20px",
    }),
    size: (size: HelpPanelSize) => {
        switch (size) {
            case "half":
                return css({ "&&": { width: "50%", maxWidth: "800px" } });
            case "third":
                return css({ "&&": { width: "40%" } });
        }
    },
};
